import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import { buildPagedSortQueryParams, SortDirection } from '../utils/pagedSortQueryParams';
import { IVolunteerActivity, isVolunteerBoardDisabledError, VolunteerActivityDateBasis } from './models';

export const VOLUNTEER_ACTIVITIES_KEY = '/volunteering/activities';

export type VolunteerActivitySortField = 'creationDate' | 'startAt';

export const useVolunteerActivitiesQuery = ({
	dateFrom,
	dateTo,
	dateBasis,
	search,
	sortBy = 'startAt',
	sort = 'desc',
	pageSize = 25,
	enabled = true,
	impersonationContext,
}: {
	dateFrom?: string;
	dateTo?: string;
	dateBasis?: VolunteerActivityDateBasis;
	search?: string;
	sortBy?: VolunteerActivitySortField;
	sort?: SortDirection;
	pageSize?: number;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
} = {}) => {
	const userSession = useUserSession();
	const hasWindow = !!dateFrom || !!dateTo;
	return useInfiniteQuery({
		enabled,
		queryKey: [
			VOLUNTEER_ACTIVITIES_KEY,
			dateFrom,
			dateTo,
			dateBasis,
			search,
			sortBy,
			sort,
			pageSize,
			impersonationContext?.account?.id,
		],
		queryFn: async ({ pageParam }) => {
			const value = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<IVolunteerActivity>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: 'volunteering/activities',
					queryParams: {
						...buildPagedSortQueryParams({ pageSize, pageToken: pageParam, sortBy, sort }),
						...(dateFrom ? { dateFrom } : {}),
						...(dateTo ? { dateTo } : {}),
						...(hasWindow ? { dateBasis: dateBasis ?? VolunteerActivityDateBasis.Shift } : {}),
						...(search ? { search } : {}),
					},
				}),
				'GET'
			);
			if (!value) {
				throw new Error('The volunteer activities response was empty');
			}
			return value;
		},
		getNextPageParam: lastPage => lastPage.pageToken,
		initialPageParam: '',
		meta: {
			toastErrorMessage: (error: Error) =>
				isVolunteerBoardDisabledError(error) ? '' : 'Failed to load volunteer activities',
		},
	});
};

export const invalidateVolunteerActivities = () =>
	reactQueryClient.invalidateQueries({ queryKey: [VOLUNTEER_ACTIVITIES_KEY] });
